import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useShowroom } from "./showroom";
import { CART_EVENT, dayTotals, groupByClient, holdPiece, initials, readHold, timeToMidnight, unholdPiece, costOf, ourPrice, clientOf } from "./businessCart";
import { SERVICE_PCT, clientTotal } from "../Data/offers";
import { encodeObj } from "../Functions";
import "./businessCart.css";

// A verified boutique's bag, read as the day's order book: pieces grouped by
// client, what tonight's payment covers, what the boutique earns, and pieces
// kept for another day. renderCard draws a piece with the usual bag card.

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "June", "July", "Aug", "Sept", "Oct", "Nov", "Dec"];
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

export function ShowroomSwitch({ compact = false }) {
    // Back to costs goes through the PIN when one is set.
    const { showroom, setShowroom, leaveShowroom } = useShowroom();
    return (
        <div className={`bz-switch${compact ? " is-compact" : ""}`} role="group" aria-label="Prices shown">
            <button type="button" className={!showroom ? "is-on" : ""} aria-pressed={!showroom} onClick={() => showroom && leaveShowroom()}>{compact ? "My cost" : "For me: my cost"}</button>
            <button type="button" className={showroom ? "is-on" : ""} aria-pressed={showroom} onClick={() => setShowroom(true)}>{compact ? "Showroom" : "Showroom: client prices"}</button>
        </div>
    );
}

export default function BusinessOrderBook({ items, renderCard, onChange }) {
    const navigate = useNavigate();
    const { biz, showroom } = useShowroom();
    const [hold, setHold] = useState(readHold);
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 30000);
        const sync = () => setHold(readHold());
        window.addEventListener(CART_EVENT, sync);
        window.addEventListener("storage", sync);
        return () => { clearInterval(t); window.removeEventListener(CART_EVENT, sync); window.removeEventListener("storage", sync); };
    }, []);

    const list = Array.isArray(items) ? items : [];
    const groups = groupByClient(list);
    const clients = groups.filter((g) => g.measured).length;
    const totals = dayTotals(list);
    const keep = (id) => onChange?.(holdPiece(id));
    const bringBack = (id) => onChange?.(unholdPiece(id));

    return (
        <div className={`bz-book${showroom ? " is-showroom" : ""}`}>
            <header className="bz-head">
                <div>
                    <p className="bz-eyebrow">{showroom ? biz?.businessName || "Your selection" : `Today's order book · ${DAYS[now.getDay()]} ${now.getDate()} ${MONTHS[now.getMonth()]}`}</p>
                    <h1 className="bz-title">
                        <span className="bz-num">{list.length}</span> {list.length === 1 ? "piece" : "pieces"} for {clients ? <span className="bz-num">{clients}</span> : "your"} <em>{clients === 1 ? "client" : "clients"}</em>
                    </h1>
                </div>
                <ShowroomSwitch />
            </header>

            {!showroom && list.length > 0 && (
                <div className="bz-due">
                    <strong>{timeToMidnight(now)}</strong>
                    <span>Pay by 11:59 pm tonight and stitching starts tomorrow morning. Anything unpaid stays here for you.</span>
                </div>
            )}

            {!showroom && totals.readyCount > 0 && (
                <dl className="bz-sums">
                    <div><dt>Ready to pay</dt><dd>{totals.readyCount} {totals.readyCount === 1 ? "piece" : "pieces"}</dd></div>
                    <div><dt>Your cost (−15%)</dt><dd>{inr(totals.cost)}</dd></div>
                    <div><dt>Clients pay you (+{SERVICE_PCT}% service)</dt><dd>{inr(totals.client)}</dd></div>
                    <div className="is-gold"><dt>You earn</dt><dd>{inr(totals.earn)}</dd></div>
                </dl>
            )}

            {groups.map((g) => (
                <section key={g.key} className={`bz-client${g.measured ? "" : " is-pending"}`} aria-label={g.name}>
                    <header className="bz-client__head">
                        <span className="bz-client__mark" aria-hidden="true">{g.measured ? initials(g.name) : "?"}</span>
                        <div className="bz-client__who">
                            <h2>{g.name}</h2>
                            <p>{g.items.length} {g.items.length === 1 ? "piece" : "pieces"}
                                {!showroom && ` · ${inr(g.items.reduce((s, it) => s + costOf(it), 0))} your cost`}
                                {` · ${inr(g.items.reduce((s, it) => s + clientTotal(ourPrice(it)), 0))} for the client, with service`}</p>
                        </div>
                        {g.measured
                            ? <span className="bz-label">Measured</span>
                            : (
                                <>
                                    {!showroom && <button type="button" className="bz-measure" onClick={() => navigate(`/measurement/select/${encodeObj(g.items[0])}`)}>Measure now →</button>}
                                    <span className="bz-warn">Measure these, or keep them for another day. Tonight's payment leaves them out.</span>
                                </>
                            )}
                    </header>
                    <div className="bz-client__pieces">
                        {g.items.map((it) => (
                            <div key={it.id} className="bz-piece">
                                {renderCard(it, list.indexOf(it))}
                                {!showroom && (
                                    <button type="button" className="bz-keep" onClick={() => keep(it.id)}>Keep for another day</button>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            ))}

            {!showroom && (
                <button type="button" className="bz-new" onClick={() => navigate("/products")}>+ A piece for a new client</button>
            )}

            {!showroom && hold.length > 0 && (
                <section className="bz-hold" aria-label="Kept for another day">
                    <h2>Kept for <em>another day</em></h2>
                    <ul>
                        {hold.map((it) => (
                            <li key={it.id}>
                                <span><b>{it.name || (it.category ? `Custom ${String(it.category).replace(/_/g, " ")}` : "Custom piece")}</b>{clientOf(it) ? ` · ${clientOf(it)}` : " · to be measured"}</span>
                                <span className="bz-hold__price">{inr(costOf(it))}</span>
                                <button type="button" onClick={() => bringBack(it.id)}>Bring back to today</button>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
}
